/**
 * Lög: bottom sheet titled with the sheet's name — own layers (radio = active layer, eye, rename,
 * share, delete), "Bæta við lagi", and "Sameiginleg lög" (owner, eye, "Afrita í mitt lag").
 *
 * Layers drawn on an older revision of the sheet carry a "Gert á eldri útgáfu" badge. Their owner
 * gets "Skoða og staðfesta"; everyone gets the "Fela lög af eldri útgáfu" switch (a per-sheet
 * filter in the engine). The logic lives in useStaleLayers, shared with the floating banner.
 */
import { BadgeCheck, Check, Copy, Eye, EyeOff, PencilLine, Plus, Share2, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import type { LayerView } from '../annotations/contract';
import { useMembers } from '../api/members';
import { Avatar } from '../components/people';
import { useToast } from '../components/toast';
import { Button, ConfirmSheet, cx, Sheet, TextInput } from '../components/ui';
import { useT } from '../i18n';
import { shareSummary as describeShares } from './layerText';
import { ShareSheet } from './ShareSheet';
import { provideStaleActions } from './staleActions';
import { StaleBadge, StaleNotice, StaleSwitch } from './StaleNotice';
import { SyncIndicator } from './SyncIndicator';
import type { Annotate } from './useAnnotate';
import { useStaleLayers } from './useStaleLayers';

/** The floating banner (kept here for existing imports; see StaleNotice.tsx). */
export { StaleBanner } from './StaleNotice';

/** Same limit as the server (SYNC_LIMITS.layerName). */
export const LAYER_NAME_MAX = 60;

function EyeToggle({ visible, onToggle, name }: { visible: boolean; onToggle: () => void; name: string }) {
  const t = useT();
  const label = visible ? t('annotate.layer_hide', { name }) : t('annotate.layer_show', { name });
  return (
    <button
      type="button"
      aria-pressed={visible}
      aria-label={label}
      title={label}
      onClick={onToggle}
      className={cx(
        'flex size-12 shrink-0 items-center justify-center rounded-xl transition',
        visible ? 'text-[var(--accent)] hover:bg-[var(--accent-soft)]' : 'text-[var(--muted)] hover:bg-[var(--surface-2)]',
      )}
    >
      {visible ? <Eye className="size-6" aria-hidden /> : <EyeOff className="size-6" aria-hidden />}
    </button>
  );
}

function SmallAction({ icon: Icon, children, onClick, danger }: { icon: typeof Eye; children: string; onClick: () => void; danger?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        'inline-flex min-h-11 items-center gap-1.5 rounded-xl px-3 text-[15px] font-semibold transition',
        danger ? 'text-[var(--danger-text)] hover:bg-red-50 dark:hover:bg-red-950/40' : 'text-[var(--accent)] hover:bg-[var(--accent-soft)]',
      )}
    >
      <Icon className="size-5" aria-hidden />
      {children}
    </button>
  );
}

export function LayerPanel({ ann }: { ann: Annotate }) {
  const t = useT();
  const toast = useToast();
  const { engine, file } = ann;
  const open = ann.layersOpen;
  const members = useMembers(open);
  const byId = useMemo(() => new Map((members.data ?? []).map((m) => [m.id, m])), [members.data]);
  const own = engine.layers.filter((l) => l.mine);
  const shared = engine.layers.filter((l) => !l.mine);

  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [toDelete, setToDelete] = useState<LayerView | null>(null);
  const [shareId, setShareId] = useState<string | null>(null);
  const shareLayer = shareId ? (engine.layers.find((l) => l.id === shareId && l.mine) ?? null) : null;

  const close = () => {
    setRenameId(null);
    setAdding(false);
    ann.setLayersOpen(false);
  };

  const shareSummary = (l: LayerView): string => describeShares(l, t, byId);

  /* ── older sheet revision ── */
  const stale = useStaleLayers(engine, file, { onLookFirst: close });
  const staleIds = stale.ids;
  const canConfirm = stale.canConfirm;
  const ownStaleCount = canConfirm ? stale.own.length : 0;
  const { review: reviewStale, hideAll: hideAllStale } = stale;
  // Actions for a StaleBanner rendered without `ann` (edit mode, see staleActions.ts).
  const bannerActions = useMemo(
    () => ({ ownCount: ownStaleCount, review: () => reviewStale(), hideAll: hideAllStale }),
    [ownStaleCount, reviewStale, hideAllStale],
  );
  useEffect(() => provideStaleActions(bannerActions), [bannerActions]);

  const submitRename = (e: FormEvent) => {
    e.preventDefault();
    const name = renameValue.trim().slice(0, LAYER_NAME_MAX);
    if (renameId && name) engine.renameLayer(renameId, name);
    setRenameId(null);
  };

  const submitAdd = (e: FormEvent) => {
    e.preventDefault();
    const name = (newName.trim() || t('annotate.layer_default_name')).slice(0, LAYER_NAME_MAX);
    const id = engine.createLayer(name);
    if (id) engine.setActiveLayer(id);
    setAdding(false);
    setNewName('');
  };

  const copy = (l: LayerView) => {
    const name = t('annotate.layer_copy_name', { name: l.name }).slice(0, LAYER_NAME_MAX);
    const id = engine.copyToMine(l.id, name);
    if (id) toast.show(t('annotate.layer_copied', { name }), { tone: 'ok' });
  };

  return (
    <>
      <Sheet open={open} onClose={close} wide title={file.title || t('annotate.layers')}>
        <div className="flex flex-col gap-5 pb-2">
          {staleIds.size > 0 ? (
            <div className="flex flex-col gap-2">
              {engine.hasStaleLayers || ownStaleCount > 0 ? <StaleNotice onReview={ownStaleCount > 0 ? reviewStale : undefined} /> : null}
              {stale.canHide ? <StaleSwitch checked={stale.hidden} count={staleIds.size} onChange={stale.setHidden} /> : null}
            </div>
          ) : null}
          <div className="flex items-center justify-between gap-2 rounded-2xl bg-[var(--surface-2)] px-3 py-1">
            <span className="text-[15px] text-[var(--muted)]">{t('annotate.layers_sync')}</span>
            <SyncIndicator sync={engine.sync} />
          </div>

          <section aria-labelledby="layers-mine">
            <h3 id="layers-mine" className="mb-2 text-[15px] font-bold tracking-wide text-[var(--muted)] uppercase">
              {t('annotate.layers_mine')}
            </h3>
            {own.length === 0 ? <p className="px-1 pb-2 text-[var(--muted)]">{t('annotate.layers_mine_empty')}</p> : null}
            <ul className="flex flex-col gap-2" role="radiogroup" aria-label={t('annotate.layers_mine')}>
              {own.map((l) => {
                const active = engine.activeLayerId === l.id;
                const isStale = staleIds.has(l.id);
                return (
                  <li key={l.id} className={cx('rounded-2xl border p-1.5', active ? 'border-[var(--accent)] bg-[var(--accent-soft)]/40' : 'border-[var(--line)]')}>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => engine.setActiveLayer(l.id)}
                        title={active ? t('annotate.layer_active') : t('annotate.layer_make_active', { name: l.name })}
                        className="flex min-h-12 min-w-0 flex-1 items-center gap-3 rounded-xl px-2 text-left"
                      >
                        <span
                          aria-hidden
                          className={cx(
                            'flex size-6 shrink-0 items-center justify-center rounded-full border-2',
                            active ? 'border-[var(--accent)]' : 'border-[var(--muted)]',
                          )}
                        >
                          {active ? <span className="size-3 rounded-full bg-[var(--accent)]" /> : null}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex min-w-0 flex-wrap items-center gap-x-2">
                            <span className="min-w-0 truncate font-semibold">{l.name}</span>
                            {isStale ? <StaleBadge /> : null}
                          </span>
                          <span className="block truncate text-[14px] text-[var(--muted)]">
                            {active ? `${t('annotate.layer_active_short')} · ` : ''}
                            {shareSummary(l)}
                            {l.pending > 0 ? ` · ${t('annotate.sync_pending', { count: l.pending })}` : ''}
                          </span>
                        </span>
                      </button>
                      <EyeToggle visible={l.visible} name={l.name} onToggle={() => engine.setLayerVisible(l.id, !l.visible)} />
                    </div>
                    {renameId === l.id ? (
                      <form onSubmit={submitRename} className="mt-1 flex flex-wrap items-end gap-2 px-2 pb-1">
                        <div className="min-w-48 flex-1">
                          <TextInput
                            label={t('annotate.layer_name')}
                            value={renameValue}
                            maxLength={LAYER_NAME_MAX}
                            autoFocus
                            onChange={(e) => setRenameValue(e.target.value)}
                            enterKeyHint="done"
                          />
                        </div>
                        <Button variant="secondary" onClick={() => setRenameId(null)}>
                          {t('common.cancel')}
                        </Button>
                        <Button type="submit" icon={Check} disabled={!renameValue.trim()}>
                          {t('common.save')}
                        </Button>
                      </form>
                    ) : (
                      <div className="flex flex-wrap gap-1 pl-10">
                        {isStale && canConfirm ? (
                          <SmallAction icon={BadgeCheck} onClick={() => reviewStale([l.id])}>
                            {t('annotate.stale_review')}
                          </SmallAction>
                        ) : null}
                        <SmallAction
                          icon={PencilLine}
                          onClick={() => {
                            setRenameId(l.id);
                            setRenameValue(l.name);
                          }}
                        >
                          {t('annotate.layer_rename')}
                        </SmallAction>
                        <SmallAction icon={Share2} onClick={() => setShareId(l.id)}>
                          {t('annotate.layer_share')}
                        </SmallAction>
                        <SmallAction icon={Trash2} danger onClick={() => setToDelete(l)}>
                          {t('annotate.layer_delete')}
                        </SmallAction>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
            <div className="mt-2">
              {adding ? (
                <form onSubmit={submitAdd} className="flex flex-wrap items-end gap-2 rounded-2xl border border-dashed border-[var(--line)] p-2">
                  <div className="min-w-48 flex-1">
                    <TextInput
                      label={t('annotate.layer_name')}
                      value={newName}
                      maxLength={LAYER_NAME_MAX}
                      placeholder={t('annotate.layer_default_name')}
                      autoFocus
                      onChange={(e) => setNewName(e.target.value)}
                      enterKeyHint="done"
                    />
                  </div>
                  <Button variant="secondary" onClick={() => setAdding(false)}>
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" icon={Plus}>
                    {t('common.add')}
                  </Button>
                </form>
              ) : (
                <Button variant="secondary" icon={Plus} onClick={() => setAdding(true)}>
                  {t('annotate.layer_add')}
                </Button>
              )}
            </div>
          </section>

          <section aria-labelledby="layers-shared">
            <h3 id="layers-shared" className="mb-2 text-[15px] font-bold tracking-wide text-[var(--muted)] uppercase">
              {t('annotate.layers_shared')}
            </h3>
            {shared.length === 0 ? <p className="px-1 text-[var(--muted)]">{t('annotate.layers_shared_empty')}</p> : null}
            <ul className="flex flex-col gap-2">
              {shared.map((l) => {
                const m = byId.get(l.ownerId);
                const owner = l.ownerInactive ? t('annotate.layer_owner_left', { name: l.ownerName }) : l.ownerName;
                return (
                  <li key={l.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-2xl border border-[var(--line)] p-2">
                    <Avatar userId={l.ownerId} name={l.ownerName} rev={m?.avatarRev ?? 0} section={m?.section ?? null} size={40} />
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 flex-wrap items-center gap-x-2">
                        <span className="min-w-0 truncate font-semibold">{l.name}</span>
                        {staleIds.has(l.id) ? <StaleBadge /> : null}
                      </div>
                      <div className="truncate text-[14px] text-[var(--muted)]">
                        {owner}
                        {l.ownerIsEditor ? ` · ${t('annotate.layer_from_leaders')}` : ''}
                      </div>
                    </div>
                    <EyeToggle visible={l.visible} name={l.name} onToggle={() => engine.setLayerVisible(l.id, !l.visible)} />
                    <Button size="sm" variant="secondary" icon={Copy} onClick={() => copy(l)} className="min-h-11">
                      {t('annotate.layer_copy')}
                    </Button>
                  </li>
                );
              })}
            </ul>
          </section>
        </div>
      </Sheet>

      <ConfirmSheet
        open={toDelete !== null}
        danger
        title={t('annotate.layer_delete_title', { name: toDelete?.name ?? '' })}
        body={t('annotate.layer_delete_body')}
        confirmLabel={t('annotate.layer_delete')}
        cancelLabel={t('common.cancel')}
        onClose={() => setToDelete(null)}
        onConfirm={() => {
          if (toDelete) engine.deleteLayer(toDelete.id);
          setToDelete(null);
        }}
      />

      <ShareSheet layer={shareLayer} file={file} engine={engine} onClose={() => setShareId(null)} />

      {stale.sheet}
    </>
  );
}
